// Onboarding Types - Based on Backend API Documentation
// This file contains all TypeScript types that match the backend API

export enum FlowType {
  LEASING = 'leasing',
  INVESTMENT = 'investment',
  INSURANCE = 'insurance',
}

export enum FlowVariant {
  PERSONA_MORAL = 'persona_moral',
  PERSONA_FISICA = 'persona_fisica', // Previously called PERSONA_FISICA_AE
}

export enum OnboardingStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  PAUSED = 'paused',
  ABANDONED = 'abandoned',
}

export enum StepStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
  REVISITED = 'revisited',
}

// Main Progress Interface
export interface OnboardingProgress {
  id: string
  status: OnboardingStatus
  currentStepKey: string | null
  currentStepOrder: number
  currentStage: number
  completionPercentage: number
  startedAt: string | null
  lastActivityAt: string | null
  totalTimeSpentSeconds: number
  user: {
    id: string
    name: string
    lastName: string
    email: string
  }
  flow: {
    id: string
    name: string
    displayName: string
    flowType: FlowType
    variant: FlowVariant
  }
  steps: OnboardingStep[]
}

// Step Definition Interface
export interface OnboardingStep {
  id: string
  status: StepStatus
  totalTimeSpentSeconds: number
  visitCount: number
  stepDefinition: {
    stepKey: string
    stepName: string
    stepOrder: number
    stage: number
    estimatedTimeMinutes: number
    routePath: string
    component: string
  }
}

// Step Form Data Interface
export interface StepFormData {
  [key: string]: any
}

// Session Information Interface
export interface SessionInfo {
  deviceInfo?: string
  browserInfo?: string
  ipAddress?: string
  metadata?: Record<string, any>
}

// Time Analytics Interface
export interface TimeAnalytics {
  totalTimeMinutes: number
  completionPercentage: number
  currentStepTime: number
  inactivityMinutes: number
  stepAnalytics: Array<{
    stepKey: string
    stepName: string
    status: StepStatus
    timeSpentMinutes: number
    estimatedMinutes: number
    visitCount: number
    isOverTime: boolean
    needsAssistance: boolean
    efficiency: number
  }>
  flags: {
    needsFollowUp: boolean
    shouldMarkAbandoned: boolean
    hasStuckSteps: boolean
  }
}

// Resume Point Interface
export interface ResumePoint {
  canResume: boolean
  currentStep: string | null
  currentStage: number
  completionPercentage: number
  lastActivityAt: string | null
  inactivityMinutes: number
  flow?: {
    id: string
    name: string
    displayName: string
    flowType: FlowType
    variant: FlowVariant
  }
  nextSteps: Array<{
    stepKey: string
    stepName: string
    estimatedMinutes: number
  }>
}

// API Response Interfaces
export interface StepCompletionResult {
  message: string
  stepKey: string
  completedAt: string
  currentProgress: {
    completionPercentage: number
    nextStep?: {
      stepKey: string
      stepName: string
      estimatedMinutes: number
    }
  }
}

export interface StepEntryResult {
  stepKey: string
  status: string
  firstEntryAt: string
  lastEntryAt: string
  visitCount: number
  message: string
}

export interface HeartbeatResult {
  message: string
  timestamp: string
}

// Metadata Interfaces
export interface OnboardingMetadata {
  timestamp?: string
  source?: string
  browser?: string
  referer?: string
  url?: string
  currentPage?: string
  action?: string
  [key: string]: unknown
}

// Admin Interfaces
export interface AdminAlert {
  id: string
  userId: string
  userName: string
  userEmail: string
  alertType: 'stuck_user' | 'abandoned_flow' | 'long_inactivity'
  message: string
  priority: 'low' | 'medium' | 'high'
  isResolved: boolean
  createdAt: string
  currentStep?: string
  inactivityMinutes?: number
  completionPercentage?: number
}

export interface AdminStatistics {
  totalUsers: number
  activeUsers: number
  completedUsers: number
  averageCompletionTime: number
  averageCompletionRate: number
  stuckUsers: number
  abandonedUsers: number
}

export interface ConversionFunnel {
  stepKey: string
  stepName: string
  totalUsers: number
  completedUsers: number
  conversionRate: number
  averageTime: number
  dropoffRate: number
}

// Step-specific Data Interfaces
export interface CompanyDataForm {
  companyName: string
  businessName?: string
  rfc: string
  businessActivity: string
  economicActivity?: string
  taxRegime?: string
  address: string
  exteriorNumber?: string
  interiorNumber?: string
  betweenStreets?: string
  city?: string
  state: string
  zipCode: string
  neighborhood?: string
  phone: string
  email: string
  website?: string
  yearEstablished?: string
  employeeCount?: string
}

export interface PersonalDataForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  curp: string
  rfc: string
  dateOfBirth: string
  address: string
}

export interface ShareholderDataForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  curp: string
  rfc: string
  sharePercentage: number
  address: string
}

export interface LegalRepresentativeForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  curp: string
  rfc: string
  position: string
  address: string
}

export interface DocumentUploadForm {
  documents: Array<{
    id: string
    name: string
    type: string
    file?: File
    url?: string
    uploaded: boolean
    required: boolean
  }>
}

export interface CiecConsultationForm {
  acceptTerms: boolean
  rfc: string
  password: string
}

export interface BureauCheckForm {
  acceptTerms: boolean
  authorizeBureauCheck: boolean
}

export interface OcularVisitForm {
  visitType: 'COMPANY_VERIFICATION' | 'SHAREHOLDER_VERIFICATION' | 'ADDRESS_VERIFICATION' | 'FULL_VERIFICATION'
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  scheduledDate: string
  contactName: string
  contactPhone: string
  contactEmail: string
  alternativeContactName?: string
  alternativeContactPhone?: string
  specialInstructions?: string
  visitAddress: string
  addressReference?: string
  addressId?: string
  companyInformationId?: string
  majorityShareholderId?: string
}

export interface QuoteReviewForm {
  acceptQuote: boolean
  requestedChanges?: string
  notes?: string
}

export interface PaymentMethodForm {
  paymentMethod: 'bank_transfer' | 'credit_card' | 'debit_card'
  bankAccount?: string
  cardNumber?: string
  cardHolderName?: string
  expiryDate?: string
}

export interface ContractSigningForm {
  preferredDate: string
  preferredTime: string
  signingLocation: 'office' | 'remote' | 'client_location'
  clientAddress?: string
  authorizedSignatory: string
  witnessName?: string
  witnessId?: string
}

// Error Types
export interface OnboardingError {
  message: string
  code?: string
  field?: string
  details?: Record<string, any>
}

// Navigation Types
export interface NavigationStep {
  stepKey: string
  stepName: string
  route: string
  isCompleted: boolean
  isCurrentStep: boolean
  canAccess: boolean
  stage: number
  order: number
}

// Context Types
export interface OnboardingContextType {
  // State
  progress: OnboardingProgress | null
  resumePoint: ResumePoint | null
  analytics: TimeAnalytics | null
  isLoading: boolean
  error: string | null
  isInitialized: boolean

  // Actions
  initializeFlow: (flowType: FlowType, variant: FlowVariant) => Promise<void>
  refreshProgress: () => Promise<void>
  completeStep: (
    stepKey: string,
    stepData: StepFormData,
    notes?: string
  ) => Promise<void>
  enterStep: (stepKey: string, metadata?: Record<string, any>) => Promise<void>
  exitStep: (stepKey: string) => Promise<void>
  checkResumePoint: () => Promise<void>
  loadAnalytics: () => Promise<void>
  clearError: () => void
}

// Hook Return Types
export interface UseOnboardingReturn {
  // Estado
  progress: OnboardingProgress | null
  currentStep: OnboardingStep | null
  isCurrentStep: boolean
  isCompleted: boolean
  canComplete: boolean

  // Loading states
  isLoading: boolean
  isCompletingStep: boolean

  // Errores
  error: string | null
  clearError: () => void

  // Acciones
  completeStep: (stepData: StepFormData, notes?: string) => Promise<boolean>

  // Métricas
  timeSpent: number
  visitCount: number
  estimatedTime: number
  isOverTime: boolean
}

export interface UseStepNavigationReturn {
  navigateToStep: (stepKey: string) => void
  navigateToNext: () => void
  navigateToPrevious: () => void
  canGoNext: boolean
  canGoPrevious: boolean
  currentStepOrder: number
  totalSteps: number
}
